import { type FC } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import IconWrapper from '@segment/matcha/components/IconWrapper'

import { CheckIcon, NamingConventionsIcon, ToolIcon } from '@segment/matcha/components/Icons/Brandbook'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Learn about data activation with Segment`,
  data: [
    {
      icon: CheckIcon,
      heading: `Use Segment Reverse ETL to sync your Snowflake customer table to Salesforce`,
      description: `Learn how to synchronize your data warehouse and Salesforce so your systems don’t diverge, and you don’t have extra work to keep them that way.`,
      button: {
        text: `Read the recipe`,
        href: `/recipes/segment-retl-sync-snowflake-salesforce/`,
      },
    },
    {
      icon: NamingConventionsIcon,
      heading: `Data-driven innovation: the CDP playbook for engineering teams`,
      description: `Learn how to use a single API to consolidate and standardize data, enabling efficient routing without the burden of integration and ETL pipeline maintenance.`,
      button: {
        text: `Get the guide`,
        href: `/learn/download/cdp-playbook-engineering-teams/`,
      },
    },
    {
      icon: ToolIcon,
      heading: `How to activate your data (and why you need to)`,
      description: `Get a complete overview of data activation and why it's essential.`,
      button: {
        text: `Read the full guide`,
        href: `https://segment.com/blog/data-activation/`,
      },
    },
  ],
}

const LearnMore: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
        </Section.Copy>

        <Section.Contents>
          <ColumnTrack columns={1} overrides={{ md: 3 }}>
            {copy.data.map(({ icon: Icon, heading, description, button }) => (
              <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
                <FeatureBox.Header>
                  <StyledIconWrapper variant="circle" elevation={1}>
                    <Icon size="large" />
                  </StyledIconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{heading}</FeatureBox.Title>
                  <FeatureBox.Text variant="pMedium">{description}</FeatureBox.Text>
                </FeatureBox.Body>
                <ButtonText href={getAbsoluteUrl(button.href, pageConfig)} iconEnd={ChevronRightIcon}>
                  {button.text}
                </ButtonText>
              </FeatureBoxBase>
            ))}
          </ColumnTrack>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const StyledIconWrapper = styled(IconWrapper)`
  width: 64px;
  height: 64px;
  padding: 6px;
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.mint[10]};
`

const StyledSection = styled(SectionBase)`
  max-width: 1280px;
`

export default LearnMore
